import { useCallback, useRef, useState } from 'react';

import {
  PauseSubscriptionResponseSchema,
  ResumeSubscriptionResponseSchema,
  type Subscription,
  type SubscriptionId,
  type SubscriptionStatus,
} from '../../api';
import { type ApiError, toApiError, type WriteRequest } from '../../lib/api/api-client';
import { useApiClient } from '../../lib/api/api-context';
import { keepsKeyForRetry, newIdempotencyKey } from '../../lib/api/idempotency-key';

/** What the trader asked for (M3 R1, R4). */
export type PauseResumeCommand = 'pause' | 'resume';

/** What the pause or resume control has done. */
export type PauseResumeState =
  | { readonly status: 'idle' }
  | { readonly status: 'busy'; readonly command: PauseResumeCommand }
  | {
      readonly status: 'done';
      readonly command: PauseResumeCommand;
      /** Pending validation requests the pause cancelled (M3 R3); 0 for a resume. */
      readonly cancelledPendingValidations: number;
      /** The status the API returned: the outcome stays shown only while E7 shows this status. */
      readonly resultStatus: SubscriptionStatus;
    }
  | { readonly status: 'failed'; readonly command: PauseResumeCommand; readonly error: ApiError };

/** Path of the pause and resume routes of one subscription (C1 `subscriptions.ts`). */
function commandPath(subscriptionId: SubscriptionId, command: PauseResumeCommand): string {
  return `/subscriptions/${encodeURIComponent(subscriptionId)}/${command}`;
}

/**
 * `POST …/pause` and `POST …/resume` for E7 (M3 §5). Each tap is one action with its own
 * `Idempotency-Key` (master X3, M3 R12); a Retry after a network failure or a 5xx sends the same
 * key, so the API runs the action at most once (S2 R11, R13). `onChanged` gets the subscription
 * the API returned, so E7 shows the new state at once and reloads its data.
 *
 * `changed: false` (already in that state, M3 R6) is a success like any other: E7 shows the
 * state the API returned.
 *
 * A second tap while a request is in flight is ignored (one action, one request), even before
 * the busy button re-renders. `reset(status)` runs whenever the status E7 shows changes (a
 * refresh, another device): it forgets a failure and its key, so a stale Retry never repeats an
 * action the trader no longer sees, and an outcome ("2 requests cancelled") whose status E7 no
 * longer shows. A status change while a request is in flight does not lose that request's key:
 * if it then fails with a network error or a 5xx, the key is kept for Retry (S2 R11, R13).
 */
export function usePauseResume(
  subscriptionId: SubscriptionId,
  onChanged: (subscription: Subscription) => void,
): {
  readonly state: PauseResumeState;
  readonly run: (command: PauseResumeCommand) => Promise<void>;
  readonly reset: (status: SubscriptionStatus) => void;
} {
  const api = useApiClient();
  const [state, setState] = useState<PauseResumeState>({ status: 'idle' });
  // The key of the last action, kept only while a retry of that same action makes sense.
  const pending = useRef<{ command: PauseResumeCommand; key: string } | null>(null);
  // Set from the tap until the answer: a double tap sends one request (master X3).
  const inFlight = useRef(false);

  const run = useCallback(
    async (command: PauseResumeCommand): Promise<void> => {
      if (inFlight.current) return;
      inFlight.current = true;
      // Kept locally: `reset` may clear `pending` while this request is in flight.
      const action =
        pending.current?.command === command
          ? pending.current
          : { command, key: newIdempotencyKey() };
      pending.current = action;
      const request: WriteRequest = {
        guard: { kind: 'authenticated', idempotencyKey: action.key },
      };
      setState({ status: 'busy', command });
      try {
        const path = commandPath(subscriptionId, command);
        const result =
          command === 'pause'
            ? await api.post(path, request, PauseSubscriptionResponseSchema)
            : {
                ...(await api.post(path, request, ResumeSubscriptionResponseSchema)),
                cancelledPendingValidations: 0,
              };
        pending.current = null;
        setState({
          status: 'done',
          command,
          cancelledPendingValidations: result.cancelledPendingValidations,
          resultStatus: result.subscription.status,
        });
        onChanged(result.subscription);
      } catch (caught) {
        const error = toApiError(caught);
        // Retry replays this very action, even if a status change reset the key meanwhile.
        pending.current = keepsKeyForRetry(error) ? action : null;
        setState({ status: 'failed', command, error });
      } finally {
        inFlight.current = false;
      }
    },
    [api, subscriptionId, onChanged],
  );

  const reset = useCallback((status: SubscriptionStatus) => {
    pending.current = null;
    setState((current) =>
      current.status === 'failed' || (current.status === 'done' && current.resultStatus !== status)
        ? { status: 'idle' }
        : current,
    );
  }, []);

  return { state, run, reset };
}
